import { AnyAction } from "redux";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
  createFailure,
  createSuccess,
  editFailure,
  editSuccess,
  listFailure,
  listRequest,
  listSuccess
} from "../actions/distributionCenters";
import { DistributionCentersActionsTypes } from "../types/DistributionCenters.reducer.types";
import { DistributionCenterType } from "../types/DistributionCenters.types";

import { SagaCustomError } from "@/common/types";
import { InventoriesService } from "@/core/libs/api";
import { messageBarTypes } from "@/core/libs/message-bar";

const abortController = new AbortController();
const { signal } = abortController;

function* createDistributionCenter({ payload }: AnyAction) {
  const { values, setSubmitting, closePanel, isAdmin } = payload;
  const service = InventoriesService("/distribution-centers", isAdmin);

  setSubmitting(true);

  try {
    const response = yield call(service.create, {
      params: { body: { ...values } },
      signal
    });

    if (response.error) {
      const error: SagaCustomError = new Error();
      error.statusCode = response.error.status;

      throw error;
    }

    yield put(
      createSuccess({
        message: `Centro de distribuição criado com sucesso.`,
        type: messageBarTypes.SUCCESS
      })
    );

    if (closePanel) {
      setTimeout(() => {
        closePanel();
      }, 1000);
    }

    // REFRESH LIST
    yield put(
      listRequest(
        { params: { excludedType: DistributionCenterType.SERVICE_POINT } },
        { isAdmin: true }
      )
    );
  } catch (error) {
    yield put(
      createFailure({
        code: error?.statusCode,
        message:
          "Não foi possível criar o centro de distribuição com os dados informados",
        type: messageBarTypes.ERROR
      })
    );
  }

  setSubmitting(false);
}

function* editDistributionCenter({ payload }: AnyAction) {
  const { values, setSubmitting, closePanel, isAdmin } = payload;
  const service = InventoriesService("/distribution-centers", isAdmin);

  setSubmitting(true);

  try {
    const response = yield call(service.edit, {
      params: { body: { ...values } },
      signal
    });

    if (response.error) {
      const error: SagaCustomError = new Error();
      error.statusCode = response.error.status;

      throw error;
    }

    yield put(
      editSuccess({
        message: `Centro de distribuição editado com sucesso.`,
        type: messageBarTypes.SUCCESS
      })
    );

    if (closePanel) {
      setTimeout(() => {
        closePanel();
      }, 1000);
    }

    // REFRESH LIST
    yield put(
      listRequest(
        { params: { excludedType: DistributionCenterType.SERVICE_POINT } },
        { isAdmin: true }
      )
    );
  } catch (error) {
    yield put(
      editFailure({
        code: error?.statusCode,
        message:
          "Não foi possível editar o centro de distribuição com os dados informados",
        type: messageBarTypes.ERROR
      })
    );
  }

  setSubmitting(false);
}

function* listDistributionCenter({ payload }: AnyAction) {
  const { filter, isAdmin } = payload;
  const service = InventoriesService("/distribution-centers", isAdmin);

  try {
    const params = {
      query: {
        page: 1,
        limit: 10,
        excludedType: DistributionCenterType.SERVICE_POINT
      }
    };

    if (filter) {
      params.query = { ...params.query, ...filter };
    }

    const response = yield call(service.list, { params, signal });

    if (response.error) {
      throw new Error();
    }

    yield put(listSuccess({ list: response.data }));
  } catch (error) {
    yield put(listFailure());
  }
}

export default all([
  takeLatest(
    DistributionCentersActionsTypes.CREATE_REQUEST,
    createDistributionCenter
  ),
  takeLatest(
    DistributionCentersActionsTypes.EDIT_REQUEST,
    editDistributionCenter
  ),
  takeLatest(
    DistributionCentersActionsTypes.LIST_REQUEST,
    listDistributionCenter
  )
]);
